import { Box, Chip, IconButton } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"
import { twMerge } from "tailwind-merge"

import {
  InventoryAdjustmentHistoryDescriptionCell,
  InventoryAdjustmentHistoryProductIdCell,
  InventoryAdjustmentHistoryStatusCell,
  InventoryAdjustmentHistoryTotalQtyChangeCell,
} from "../components/InventoryAdjustmentHistoryRow"
import { ADJUSTMENT_REASON_TO_LABEL, ADJUSTMENT_TYPE_TO_CHIP_CLASS, ADJUSTMENT_TYPE_TO_LABEL } from "../constants"

import { DATE_FORMAT } from "@/constants/formats"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"

interface UseInventoryAdjustmentHistoryColumnsProps {
  onDocumentsOpen: (item: AdjustmentHistoryItem) => void
}

export const useInventoryAdjustmentHistoryColumns = ({
  onDocumentsOpen,
}: UseInventoryAdjustmentHistoryColumnsProps): ColumnDef<AdjustmentHistoryItem>[] => {
  return [
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => dayjs(row.original.date).format(DATE_FORMAT.US),
      enableSorting: true,
    },
    {
      accessorKey: "adjustmentReferenceNbr",
      header: "Adjustment #",
      cell: ({ row }) => row.original.adjustmentReferenceNbr,
      enableSorting: false,
    },
    {
      accessorKey: "warehouse",
      header: "Warehouse",
      cell: ({ row }) => [row.original.warehouse.as400Id, row.original.warehouse.description].filter(Boolean).join(" "),
      enableSorting: true,
    },
    {
      accessorKey: "productId",
      header: "Product ID",
      cell: ({ row }) => <InventoryAdjustmentHistoryProductIdCell product={row.original} />,
      enableSorting: false,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => <InventoryAdjustmentHistoryDescriptionCell description={row.original.description} />,
      enableSorting: false,
    },
    {
      accessorKey: "adjustmentType",
      header: "Type",
      cell: ({ row }) => (
        <Chip
          className={twMerge(
            "h-6 rounded-sm text-xs font-medium",
            ADJUSTMENT_TYPE_TO_CHIP_CLASS[row.original.adjustmentType]
          )}
          label={ADJUSTMENT_TYPE_TO_LABEL[row.original.adjustmentType]}
          size='small'
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "totalQtyChange",
      header: "Total Qty Change",
      cell: ({ row }) => <InventoryAdjustmentHistoryTotalQtyChangeCell qtyChange={row.original.totalQtyChange} />,
      enableSorting: false,
    },
    {
      accessorKey: "reason",
      header: "Reason",
      cell: ({ row }) => (row.original.reason ? ADJUSTMENT_REASON_TO_LABEL[row.original.reason] : ""),
      enableSorting: false,
    },
    {
      accessorKey: "adjustedBy",
      header: "Adjusted By",
      cell: ({ row }) => row.original.adjustedBy?.displayName ?? "",
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <InventoryAdjustmentHistoryStatusCell status={row.original.status} />,
      enableSorting: true,
    },
    {
      accessorKey: "docs",
      header: "Docs",
      size: 56,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.hasDocuments ? (
          <Box className='flex items-center justify-center'>
            <IconButton size='small' onClick={() => onDocumentsOpen(row.original)}>
              <DraftOutlined />
            </IconButton>
          </Box>
        ) : null,
    },
    {
      accessorKey: "actions",
      header: "",
      size: 56,
      enableSorting: false,
      cell: ({ row }) => (
        <Box className='flex items-center justify-end'>
          <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      ),
    },
  ]
}
